import InvestorInvitationProductResponseResource from "common/resources/InvestorInvitation/InvestorInvitationProductResponseResource";
import { useHasRules } from "components/materials/HasRules";
import ModuleConfig from "configs/ModuleConfig";
import { ReactNode, createContext, useContext, useEffect, useState } from "react";
import InvestorInvitationService from "services/InvestorInvitationService";
import { container } from "tsyringe";

import { UserContext } from "./User";

export type IInvestorPortalAccessContext = {
	/**
	 * Produits du cabinet ouverts au portail investisseur. `null` tant que la réponse n'est pas arrivée ;
	 * vide si le cabinet n'en distribue aucun : la création d'invitations est alors indisponible.
	 */
	products: InvestorInvitationProductResponseResource[] | null;
};

type IProps = {
	children: ReactNode;
};

export const InvestorPortalAccessContext = createContext<IInvestorPortalAccessContext>({ products: null });

const investorInvitationService = container.resolve(InvestorInvitationService);
const isModuleEnabled = container.resolve(ModuleConfig).get().modules.pages.InvestorInvitations.enabled;

/**
 * La création reste réservée aux conseillers dont le cabinet distribue un produit ouvert au portail.
 * La liste est demandée une fois par utilisateur connecté, puis partagée par le menu, la route et la page.
 */
export function InvestorPortalAccessProvider(props: IProps) {
	const { user } = useContext(UserContext);
	const isAdvisor = useHasRules({ AND: { access_platform__cgp: true } });
	const [products, setProducts] = useState<IInvestorPortalAccessContext["products"]>(null);
	const userId = user?.id ?? null;

	useEffect(() => {
		if (!userId) {
			setProducts(null);
			return;
		}
		if (!isModuleEnabled || !isAdvisor) {
			setProducts([]);
			return;
		}

		let isCurrent = true;
		setProducts(null);
		investorInvitationService
			.getProducts()
			.then((eligible) => isCurrent && setProducts(eligible))
			.catch(() => isCurrent && setProducts([]));
		return () => {
			isCurrent = false;
		};
	}, [isAdvisor, userId]);

	return <InvestorPortalAccessContext.Provider value={{ products }}>{props.children}</InvestorPortalAccessContext.Provider>;
}

/** La consultation est indépendante des produits éligibles à une nouvelle invitation. */
export function useInvestorPortalAccess() {
	const { products } = useContext(InvestorPortalAccessContext);
	const { user } = useContext(UserContext);
	const isAdvisor = useHasRules({ AND: { access_platform__cgp: true } });
	const isMiddleOffice = useHasRules({ AND: { access_platform__middle_office: true } });
	return {
		products,
		isLoading: products === null,
		canView: isModuleEnabled && Boolean(user) && (isAdvisor || isMiddleOffice),
		canInvite: isModuleEnabled && isAdvisor && products !== null && products.length > 0,
	};
}
